import React, { useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import "../ApprovalTeamsList/DeleteApprovalTeamModal.css";

const MODE_REASSIGN = "reassign";
const MODE_NONE = "none";

// Shown when unchecking "Allow Approvals" would empty out an Approval Team.
// Makes no API calls itself — onConfirm hands the choices back to RoleForm.
export default function RemoveApprovalPermissionModal({
  teamsToDelete = [],
  teamsToTrim = [],
  teamsByCompany = {},
  onCancel,
  onConfirm,
}) {
  const [choices, setChoices] = useState(() => {
    const initial = {};
    teamsToDelete.forEach(({ team }) => {
      const otherTeams = getOtherTeams(team, teamsToDelete);
      initial[team.uuid] =
        otherTeams.length > 0
          ? { mode: MODE_REASSIGN, reassignTo: otherTeams[0].uuid }
          : { mode: MODE_NONE, reassignTo: "" };
    });
    return initial;
  });

  // Valid reassignment targets: same company, not itself being deleted.
  function getOtherTeams(team, allDeletes) {
    const deletingUuids = new Set(allDeletes.map((d) => d.team.uuid));
    return (teamsByCompany[team.company_uuid] || []).filter(
      (t) => t.uuid !== team.uuid && !deletingUuids.has(t.uuid)
    );
  }

  const setMode = (teamUuid, mode) => {
    setChoices((prev) => ({
      ...prev,
      [teamUuid]: { ...prev[teamUuid], mode },
    }));
  };

  const setReassignTo = (teamUuid, reassignTo) => {
    setChoices((prev) => ({
      ...prev,
      [teamUuid]: { ...prev[teamUuid], reassignTo },
    }));
  };

  const handleConfirm = () => {
    const reassignmentChoicesByTeamUuid = {};
    teamsToDelete.forEach(({ team }) => {
      const choice = choices[team.uuid];
      reassignmentChoicesByTeamUuid[team.uuid] =
        choice?.mode === MODE_REASSIGN && choice.reassignTo
          ? choice.reassignTo
          : null;
    });
    onConfirm(reassignmentChoicesByTeamUuid);
  };

  return (
    <div className="jfm-overlay" onClick={onCancel}>
      <div
        className="jfm-container dtm-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dtm-header">
          <div className="dtm-header-icon">
            <AlertTriangle size={20} />
          </div>
          <div>
            <h3 className="dtm-header-title">Remove Allow Approvals</h3>
            <p className="dtm-header-subtitle">
              Removing this permission affects existing Approval Teams
            </p>
          </div>
          <button
            className="jfm-close"
            onClick={onCancel}
            style={{ marginLeft: "auto" }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="dtm-body">
          {teamsToDelete.map(({ team }) => {
            const otherTeams = getOtherTeams(team, teamsToDelete);
            const choice = choices[team.uuid] || {
              mode: MODE_NONE,
              reassignTo: "",
            };
            return (
              <div key={team.uuid}>
                <p className="dtm-prompt">
                  Removing this permission will remove all members of Approval
                  Team <strong>"{team.name}"</strong>. How do you want to
                  proceed?
                </p>

                <label
                  className={`dtm-option${
                    choice.mode === MODE_REASSIGN ? " dtm-option--selected" : ""
                  }${otherTeams.length === 0 ? " dtm-option--disabled" : ""}`}
                >
                  <div className="dtm-option-head">
                    <input
                      type="radio"
                      name={`approval-permission-mode-${team.uuid}`}
                      checked={choice.mode === MODE_REASSIGN}
                      disabled={otherTeams.length === 0}
                      onChange={() => setMode(team.uuid, MODE_REASSIGN)}
                    />
                    <span className="dtm-option-title">
                      Delete Approval Team and reassign to
                    </span>
                  </div>
                  {choice.mode === MODE_REASSIGN && otherTeams.length > 0 && (
                    <select
                      className="dtm-option-select"
                      value={choice.reassignTo}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setReassignTo(team.uuid, e.target.value)}
                    >
                      {otherTeams.map((t) => (
                        <option key={t.uuid} value={t.uuid}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {otherTeams.length === 0 && (
                    <p className="dtm-option-subtitle">
                      No other approval teams exist for this company.
                    </p>
                  )}
                </label>

                <label
                  className={`dtm-option${
                    choice.mode === MODE_NONE ? " dtm-option--selected" : ""
                  }`}
                >
                  <div className="dtm-option-head">
                    <input
                      type="radio"
                      name={`approval-permission-mode-${team.uuid}`}
                      checked={choice.mode === MODE_NONE}
                      onChange={() => setMode(team.uuid, MODE_NONE)}
                    />
                    <span className="dtm-option-title">
                      Delete Approval Team, no reassignment
                    </span>
                  </div>
                  <p className="dtm-option-subtitle">
                    Remove the approval team from all pending entries and linked
                    jobs
                  </p>
                </label>
              </div>
            );
          })}

          {teamsToTrim.length > 0 && (
            <div>
              <p className="dtm-prompt">
                These teams keep other members and will just have the affected
                user(s) removed:
              </p>
              {teamsToTrim.map(({ team, removedCount }) => (
                <p key={team.uuid} className="dtm-option-subtitle">
                  {team.name}: {removedCount} member
                  {removedCount === 1 ? "" : "s"} removed
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="jfm-footer">
          <div />
          <div className="jfm-footer-actions">
            <button className="jfm-cancel" onClick={onCancel}>
              Cancel Removal
            </button>
            <button className="dtm-delete-btn" onClick={handleConfirm}>
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
